import { FieldTypePreview } from '@monorepo/shared/componentsV2/fieldDataType/FieldTypePreview/FieldTypePreview';
import { MenuOption } from '@monorepo/shared/componentsV2/menus/Menu';
import CalendarStarIcon from '@svg/calendar-star.svg';
import {
  FormTemplateAttribute,
  FormTemplateFieldResponse,
  FormTemplateFieldTypes,
} from 'mapistry-shared';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { FormTemplateFieldPreviewQualifiers } from './FormTemplateFieldPreviewQualifiers';
import { getFieldTypeDisplay } from './getFieldTypeDisplay';
import { FieldConditionHelpers } from './hooks/useFieldConditionHelpers';
import { EditableFormTemplateField } from './types';

const canFieldTypeBeConditionsTrigger = (fieldType: FormTemplateFieldTypes) =>
  fieldType !== FormTemplateFieldTypes.EXPLANATION_AREA;

interface FieldTypeFactoryProps {
  field: FormTemplateFieldResponse | EditableFormTemplateField;
  fieldConditionHelpers: FieldConditionHelpers;
  openEditFieldModal: () => void;
  openEditFieldConditionsModal: () => void;
  openMoveInspectionDateModal: () => void;
  onCopyField: () => void;
  onDeleteField: () => void;
  isEditingDisabled: boolean;
  isMovingDisabled: boolean;
  movingDisabledReason: string;
  isDeletingDisabled: boolean;
  deletingDisabledReason: string;
}

const StyledFormTemplateField = styled(FieldTypePreview)`
  margin: 0;

  /* so elements aren't see-through while being dragged over one another  */
  background-color: ${({ theme }) => theme.colors.white};
`;

export function FieldTypeFactory({
  field,
  fieldConditionHelpers,
  openEditFieldModal,
  openEditFieldConditionsModal,
  openMoveInspectionDateModal,
  onCopyField,
  onDeleteField,
  isMovingDisabled,
  movingDisabledReason,
  isEditingDisabled,
  isDeletingDisabled,
  deletingDisabledReason,
}: FieldTypeFactoryProps) {
  const isInspectionDate = useMemo(
    () => field.attributes?.includes(FormTemplateAttribute.IS_INSPECTION_DATE),
    [field],
  );

  const iconDetails = useMemo(() => {
    if (isInspectionDate) {
      return {
        icon: <CalendarStarIcon />,
        tooltip: 'This field is marked as inspection date',
      };
    }
    const { icon, label } = getFieldTypeDisplay(field.type);
    return { icon, tooltip: label };
  }, [field.type, isInspectionDate]);

  const actionMenuOptions = useMemo<MenuOption[]>(() => {
    const options: MenuOption[] = [
      {
        displayName: 'Edit field',
        onClick: openEditFieldModal,
        disabled: isEditingDisabled,
      },
    ];

    if (canFieldTypeBeConditionsTrigger(field.type)) {
      options.push({
        displayName: 'Edit conditions',
        onClick: openEditFieldConditionsModal,
        disabled: isEditingDisabled,
      });
    }

    if (isInspectionDate) {
      options.push({
        displayName: 'Move',
        onClick: openMoveInspectionDateModal,
        disabled: isMovingDisabled,
        tooltip: movingDisabledReason,
      });
    }

    options.push({
      displayName: 'Copy',
      onClick: onCopyField,
      disabled: isEditingDisabled,
    });

    options.push({
      displayName: 'Delete',
      onClick: onDeleteField,
      disabled: isDeletingDisabled,
      color: 'danger',
      tooltip: deletingDisabledReason,
    });

    return options;
  }, [
    openEditFieldModal,
    isEditingDisabled,
    field.type,
    isInspectionDate,
    onCopyField,
    onDeleteField,
    isDeletingDisabled,
    deletingDisabledReason,
    openEditFieldConditionsModal,
    openMoveInspectionDateModal,
    isMovingDisabled,
    movingDisabledReason,
  ]);

  return (
    <StyledFormTemplateField
      icon={iconDetails.icon}
      iconTooltip={iconDetails.tooltip}
      fieldName={field.name}
      outerElement="div"
      actionMenuOptions={actionMenuOptions}
      isDisabled={isEditingDisabled}
      isRequired={field.isRequired}
      qualifier={
        <FormTemplateFieldPreviewQualifiers
          field={field}
          fieldConditionHelpers={fieldConditionHelpers}
        />
      }
    />
  );
}
